// frontend/src/modules/Confirmations/components/CreateConfirmationModal.tsx
import { useState, useEffect } from 'react';
import {
  Modal, Button, Stack, Group, Text, TextInput, Textarea, NumberInput,
  Select, Card, Grid, Divider, ActionIcon, ThemeIcon, Tabs, Alert, Center,
  Switch, Badge, ScrollArea, Box, Checkbox, SegmentedControl, Progress,
  Image, Paper, FileButton, Tooltip, Loader
} from '@mantine/core';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { notifications } from '@mantine/notifications';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconPlus, IconTrash, IconCheck, IconX, IconHome,
  IconCalendar, IconPhone, IconMail,
  IconBuilding, IconUsers, IconPlane, IconCar, IconNote,
  IconAlertCircle, IconCurrencyBaht, IconBolt, IconDroplet,
  IconCash, IconBabyCarriage, IconUserCircle, IconBell,
  IconUpload, IconEdit, IconPhoto
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  confirmationsApi, CreateConfirmationDTO, ConfirmationGuest,
  ConfirmationChild, ConfirmationProperty, FieldVisibility,
  ConfirmationPayment, ConfirmationReminder, PaymentSummary, ExpectedAmount
} from '@/api/confirmations.api';
import api from '@/api/axios';
import dayjs from 'dayjs';

// Extracted outside component to prevent re-creation on every render (fixes focus loss)
const VisField = ({ field, fieldVisibility, toggleVisibility, children }: {
  field: string;
  fieldVisibility: FieldVisibility;
  toggleVisibility: (field: string) => void;
  children: React.ReactNode;
}) => (
  <Group gap={4} align="flex-start" wrap="nowrap">
    <Checkbox
      size="xs"
      mt={28}
      checked={fieldVisibility[field] !== false}
      onChange={() => toggleVisibility(field)}
      title={fieldVisibility[field] !== false ? 'Visible in document' : 'Hidden in document'}
      styles={{ input: { cursor: 'pointer' } }}
    />
    <Box style={{ flex: 1 }}>{children}</Box>
  </Group>
);

interface CreateConfirmationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  onCreated?: () => void;
  mode?: 'create' | 'edit';
  confirmationId?: number;
}

const DEFAULT_ARRIVAL_TIME = '15:00';
const DEFAULT_DEPARTURE_TIME = '12:00';
const DEFAULT_CHECK_IN_TIME = '15:00';
const DEFAULT_CHECK_OUT_TIME = '12:00';
const DEFAULT_ELECTRICITY_RATE = 7;
const DEFAULT_WATER_RATE = 0;
const DEFAULT_DEPOSIT_AMOUNT = 20000;
const DEFAULT_CANCELLATION_POLICY = 'Please note that once a reservation is confirmed and payment has been received, the booking is considered final. All confirmed bookings are strictly non-cancellable, non-refundable, and non-amendable.';
const DEFAULT_WELCOME_MESSAGE = "Thank you for making a reservation at our Villa. We're delighted to confirm the details for your upcoming stay.";
const DEFAULT_NOTICE_CONTENT = `Your reservation includes the exclusive use of the villa, complimentary water supply, high-speed Wi-Fi, basic housekeeping services, as well as the routine maintenance of your private pool and lush gardens.
Check-in time is from 3:00 PM onwards, and we kindly request that you check out by 12:00 PM (Noon).
To ensure a worry-free stay, a fully refundable security deposit of [?] is required upon check-in. This deposit will be promptly returned to you upon your departure, provided there are no damages or outstanding incidental charges.
Please kindly note that electricity consumption are not included in the rental rate. Electricity will be charged based on your actual consumption at 7 THB per unit (kWh).
To ensure your utmost comfort, the villa strictly accommodates a maximum of [?] guests, comprising up to [?] adults and [?] children.
Any illegal or immoral activities—including but not limited to gambling, prostitution, the use of illicit drugs, and the possession of hazardous materials or weapons—are strictly prohibited on the premises.`;
const DEFAULT_INCLUDED_SERVICES = [
  'To treat your palate, your reservation includes complimentary Private Chef services and a rich, locally-inspired daily breakfast!',
  'Includes one complimentary round-trip airport transfer (applicable for one 10 seat van per way, for guests arriving/departing on the same flight)',
];
const DEFAULT_PHONE = '+66 950956670';
const DEFAULT_EMAIL = 'phuket@warmphuket.com';
const DEFAULT_COMPANY = 'WARM+';
const DATEPICKER_PORTAL_ID = 'confirmation-datepicker-portal';

const PAYMENT_TERMS_OPTIONS = [
  {
    value: '100_full',
    label: '100% Full Payment',
    text: 'Payment Terms & Schedule: To secure your beautiful villa reservation, please kindly arrange the payment as follows:\n\n100% Full Payment: The total amount of the reservation is required upon receiving this confirmation to secure your dates.\n\nPlease note: The booking will be fully confirmed only upon receipt of the required payment. If the payment is not received by [Insert Date/Time], the dates will be automatically released.'
  },
  {
    value: '50_50_30days',
    label: '50% Deposit & 50% Balance (30 days)',
    text: 'Payment Terms & Schedule: To secure your beautiful villa reservation, please kindly arrange the payment as follows:\n\n50% Deposit & 50% Balance: A 50% deposit is required to secure your booking. The remaining 50% balance must be settled at least 30 days prior to your arrival.\n\nPlease note: The booking will be fully confirmed only upon receipt of the required payment. If the payment is not received by [Insert Date/Time], the dates will be automatically released.'
  },
  {
    value: '50_50_7days',
    label: '50% Deposit & 50% Balance (7 days)',
    text: 'Payment Terms & Schedule: To secure your beautiful villa reservation, please kindly arrange the payment as follows:\n\n50% Deposit & 50% Balance: A 50% deposit is required to secure your booking. The remaining 50% balance must be settled at least 7 days prior to your arrival.\n\nPlease note: The booking will be fully confirmed only upon receipt of the required payment. If the payment is not received by [Insert Date/Time], the dates will be automatically released.'
  },
  {
    value: '50_50_on_arrival',
    label: '50% Deposit & 50% Upon Arrival',
    text: 'Payment Terms & Schedule: To secure your beautiful villa reservation, please kindly arrange the payment as follows:\n\n50% Deposit & 50% Upon Arrival: A 50% deposit is required to secure your booking. The remaining 50% balance can be settled upon check-in at the villa.\n\nPlease note: The booking will be fully confirmed only upon receipt of the required payment. If the payment is not received by [Insert Date/Time], the dates will be automatically released.'
  },
];

const PAYMENT_STATUS_OPTIONS = [
  {
    value: 'fully_paid',
    label: '100% Fully Paid',
    text: 'Status: 100% Fully Paid. We acknowledge with thanks the safe receipt of your full payment. Your beautiful villa reservation is now fully confirmed. We look forward to welcoming you soon!'
  },
  {
    value: 'deposit_50_30days',
    label: '50% Deposit Received (30 days balance)',
    text: 'Status: 50% Deposit Received. We acknowledge with thanks the safe receipt of your deposit. Your reservation is now fully confirmed. Outstanding Balance: The remaining 50% balance is kindly due on or before [date] (30 days prior to your arrival)'
  },
  {
    value: 'deposit_50_7days',
    label: '50% Deposit Received (7 days balance)',
    text: 'Status: 50% Deposit Received. We acknowledge with thanks the safe receipt of your deposit. Your reservation is now fully confirmed. Outstanding Balance: The remaining 50% balance is kindly due on or before [date] (7 days prior to your arrival)'
  },
  {
    value: 'deposit_50_on_arrival',
    label: '50% Deposit Received (upon arrival)',
    text: 'Status: 50% Deposit Received. We acknowledge with thanks the safe receipt of your deposit. Your reservation is now fully confirmed. Outstanding Balance: The remaining 50% balance will be settled upon your arrival on [date], strictly prior to villa access and check-in.'
  },
];

// All visibility field keys
const ALL_VISIBILITY_FIELDS = [
  'property_name', 'property_address', 'from_company_name', 'from_telephone', 'from_email',
  'confirmation_date', 'arrival_date', 'departure_date', 'arrival_time', 'departure_time',
  'check_in_time', 'check_out_time', 'room_type', 'rate_type', 'rate_amount', 'num_rooms',
  'num_guests', 'deposit_amount', 'currency', 'pick_up_service', 'drop_off_service',
  'arrival_flight', 'departure_flight', 'electricity_rate', 'water_rate',
  'notice_content', 'cancellation_policy', 'payment_terms', 'payment_status_text', 'welcome_message', 'remarks', 'agent_name'
];

const getDefaultVisibility = (): FieldVisibility => {
  const v: FieldVisibility = {};
  ALL_VISIBILITY_FIELDS.forEach(f => { v[f] = true; });
  return v;
};

const CreateConfirmationModal = ({
  visible, onCancel, onSuccess, onCreated, mode = 'create', confirmationId
}: CreateConfirmationModalProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<ConfirmationProperty[]>([]);
  const [propertyInputMode, setPropertyInputMode] = useState<'select' | 'manual'>('select');

  // Field visibility
  const [fieldVisibility, setFieldVisibility] = useState<FieldVisibility>(getDefaultVisibility());

  const toggleVisibility = (field: string) => {
    setFieldVisibility(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Channel & Name
  const [channel, setChannel] = useState<string | null>(null);
  const [agentName, setAgentName] = useState('');

  // Payment status
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success'>('pending');

  // Property
  const [propertyId, setPropertyId] = useState<number | null>(null);
  const [propertyName, setPropertyName] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [roomType, setRoomType] = useState('');
  const [numRooms, setNumRooms] = useState<number>(1);
  const [numGuests, setNumGuests] = useState<number>(2);

  // Contact
  const [fromCompanyName, setFromCompanyName] = useState(DEFAULT_COMPANY);
  const [fromTelephone, setFromTelephone] = useState(DEFAULT_PHONE);
  const [fromEmail, setFromEmail] = useState(DEFAULT_EMAIL);

  // Dates
  const [confirmationDate, setConfirmationDate] = useState<Date | null>(new Date());
  const [arrivalDate, setArrivalDate] = useState<Date | null>(null);
  const [departureDate, setDepartureDate] = useState<Date | null>(null);
  const [arrivalTime, setArrivalTime] = useState(DEFAULT_ARRIVAL_TIME);
  const [departureTime, setDepartureTime] = useState(DEFAULT_DEPARTURE_TIME);
  const [checkInTime, setCheckInTime] = useState(DEFAULT_CHECK_IN_TIME);
  const [checkOutTime, setCheckOutTime] = useState(DEFAULT_CHECK_OUT_TIME);

  // Booking
  const [rateType, setRateType] = useState<'daily' | 'monthly' | 'period'>('daily');
  const [rateAmount, setRateAmount] = useState<number>(0);
  const [depositAmount, setDepositAmount] = useState<number>(DEFAULT_DEPOSIT_AMOUNT);
  const [currency, setCurrency] = useState('THB');

  // Services
  const [pickUpService, setPickUpService] = useState(false);
  const [dropOffService, setDropOffService] = useState(false);
  const [arrivalFlight, setArrivalFlight] = useState('');
  const [departureFlight, setDepartureFlight] = useState('');

  // Guests
  const [guests, setGuests] = useState<ConfirmationGuest[]>([
    { guest_name: '', passport_number: '', passport_country: '', phone: '', email: '' }
  ]);
  const [numChildren, setNumChildren] = useState(0);
  const [children, setChildren] = useState<ConfirmationChild[]>([]);

  // Content
  const [noticeContent, setNoticeContent] = useState('');
  const [cancellationPolicy, setCancellationPolicy] = useState(DEFAULT_CANCELLATION_POLICY);
  const [paymentTermsOption, setPaymentTermsOption] = useState<string | null>(null);
  const [paymentTermsText, setPaymentTermsText] = useState('');
  const [paymentDeadline, setPaymentDeadline] = useState('');
  const [paymentStatusOption, setPaymentStatusOption] = useState<string | null>(null);
  const [paymentStatusText, setPaymentStatusText] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState(DEFAULT_WELCOME_MESSAGE);
  const [includedServices, setIncludedServices] = useState<string[]>([...DEFAULT_INCLUDED_SERVICES]);
  const [remarks, setRemarks] = useState('');
  const [electricityRate, setElectricityRate] = useState(DEFAULT_ELECTRICITY_RATE);
  const [waterRate, setWaterRate] = useState(DEFAULT_WATER_RATE);

  // Reminders (for creation)
  const [initialReminders, setInitialReminders] = useState<{ type: string; days?: number }[]>([]);

  // Tab state
  const [activeTab, setActiveTab] = useState<string | null>('basic');

  // Payment/Reminder state (for edit mode - embedded)
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [paymentsList, setPaymentsList] = useState<ConfirmationPayment[]>([]);
  const [remindersList, setRemindersList] = useState<ConfirmationReminder[]>([]);
  const [scheduleList, setScheduleList] = useState<any[]>([]);

  // Schedule items for creation (before document exists)
  const [scheduleItems, setScheduleItems] = useState<{due_date: Date | null, amount: number, currency: string, description: string, show_in_doc: boolean}[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [_editingPaymentId, _setEditingPaymentId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, currency: 'THB', payment_date: new Date(), payment_method: 'bank_transfer', notes: '' });
  const [paymentAlreadyPaid, setPaymentAlreadyPaid] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingExpected, setEditingExpected] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null);
  const [editScheduleForm, setEditScheduleForm] = useState({ amount: 0, currency: 'THB', due_date: null as Date | null, description: '', show_in_document: true });
  const [expectedForm, setExpectedForm] = useState<ExpectedAmount[]>([{ amount: 0, currency: 'THB' }]);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const defaultReminderForm = {
    reminder_type: 'days_before' as string, days_before: 7, custom_date: null as Date | null,
    comment: '', is_enabled: true, recurrence_type: 'none' as string, recurrence_interval: 1,
    recurrence_end_date: null as Date | null, recurrence_max_count: null as number | null,
    day_of_month: 1 as number, month_of_year: 1 as number,
    recurrence_day_of_week: null as number | null,
  };
  const [reminderForm, setReminderForm] = useState({ ...defaultReminderForm });

  const CURRENCIES = ['THB', 'USD', 'EUR', 'RUB', 'CNY', 'GBP'];
  // Payment methods removed — payments now use schedule items only

  // getMethodIcon removed — payments managed via schedule

  // Success state
  const [createdConfirmationNumber, setCreatedConfirmationNumber] = useState('');

  // Create datepicker portal with high z-index to appear above Mantine modals
  useEffect(() => {
    let el = document.getElementById(DATEPICKER_PORTAL_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = DATEPICKER_PORTAL_ID;
      document.body.appendChild(el);
    }
    el.style.position = 'relative';
    el.style.zIndex = '10000';
  }, []);

  useEffect(() => {
    if (visible) {
      fetchProperties();
      fetchPartnerDefaults();
      if (mode === 'edit' && confirmationId) {
        loadForEdit(confirmationId);
      } else {
        resetForm();
      }
    }
  }, [visible]);

  const fetchProperties = async () => {
    try {
      const response = await confirmationsApi.getProperties();
      setProperties(response.data.data || []);
    } catch (e) { console.error('Error fetching properties:', e); }
  };

  const fetchPartnerDefaults = async () => {
    try {
      const response = await api.get('/partners/current');
      if (response.data.success && response.data.data) {
        const p = response.data.data;
        if (mode === 'create') {
          setFromCompanyName(p.partner_name || '');
          setFromTelephone(p.phone || DEFAULT_PHONE);
          setFromEmail(p.email || DEFAULT_EMAIL);
        }
      }
    } catch { /* ignore */ }
  };

  const loadForEdit = async (id: number) => {
    try {
      setLoading(true);
      const response = await confirmationsApi.getById(id);
      const c = response.data.data;

      setChannel(c.channel || null);
      setAgentName(c.agent_name || '');
      setPaymentStatus(c.payment_status || 'pending');
      setPropertyId(c.property_id || null);
      setPropertyInputMode(c.property_id ? 'select' : 'manual');
      setPropertyName(c.property_name || '');
      setPropertyAddress(c.property_address || '');
      setFromCompanyName(c.from_company_name || '');
      setFromTelephone(c.from_telephone || DEFAULT_PHONE);
      setFromEmail(c.from_email || DEFAULT_EMAIL);
      setConfirmationDate(c.confirmation_date ? new Date(c.confirmation_date) : new Date());
      setArrivalDate(c.arrival_date ? new Date(c.arrival_date) : null);
      setDepartureDate(c.departure_date ? new Date(c.departure_date) : null);
      setArrivalTime(c.arrival_time || DEFAULT_ARRIVAL_TIME);
      setDepartureTime(c.departure_time || DEFAULT_DEPARTURE_TIME);
      setCheckInTime(c.check_in_time || DEFAULT_CHECK_IN_TIME);
      setCheckOutTime(c.check_out_time || DEFAULT_CHECK_OUT_TIME);
      setRoomType(c.room_type || '');
      setRateType(c.rate_type === 'monthly' ? 'monthly' : c.rate_type === 'for_period' ? 'period' : 'daily');
      setRateAmount(c.rate_amount || 0);
      setNumRooms(c.num_rooms || 1);
      setNumGuests(c.num_guests || 2);
      setDepositAmount(c.deposit_amount || DEFAULT_DEPOSIT_AMOUNT);
      setCurrency(c.currency || 'THB');
      setPickUpService(!!c.pick_up_service);
      setDropOffService(!!c.drop_off_service);
      setArrivalFlight(c.arrival_flight || '');
      setDepartureFlight(c.departure_flight || '');
      setNoticeContent(c.notice_content || DEFAULT_NOTICE_CONTENT);
      setIncludedServices(c.included_services ? (typeof c.included_services === 'string' ? JSON.parse(c.included_services) : c.included_services) : [...DEFAULT_INCLUDED_SERVICES]);
      setCancellationPolicy(c.cancellation_policy || DEFAULT_CANCELLATION_POLICY);
      setPaymentTermsOption(c.payment_terms_option || null);
      setPaymentTermsText(c.payment_terms_text || '');
      setPaymentDeadline(c.payment_deadline || '');
      setPaymentStatusOption(c.payment_status_option || null);
      setPaymentStatusText(c.payment_status_text || '');
      setWelcomeMessage(c.welcome_message || DEFAULT_WELCOME_MESSAGE);
      setRemarks(c.remarks || '');
      setElectricityRate(c.electricity_rate ?? DEFAULT_ELECTRICITY_RATE);
      setWaterRate(c.water_rate ?? DEFAULT_WATER_RATE);
      setNumChildren(c.num_children || 0);
      setGuests(c.guests?.length > 0 ? c.guests : [{ guest_name: '', passport_number: '', passport_country: '', phone: '', email: '' }]);
      setChildren(c.children || []);
      setFieldVisibility(c.field_visibility || getDefaultVisibility());
    } catch (e) {
      console.error('Error loading confirmation:', e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setActiveTab('basic');
    setChannel(null);
    setAgentName('');
    setPaymentStatus('pending');
    setPropertyId(null);
    setPropertyInputMode('select');
    setPropertyName('');
    setPropertyAddress('');
    setFromCompanyName(DEFAULT_COMPANY);
    setFromTelephone(DEFAULT_PHONE);
    setFromEmail(DEFAULT_EMAIL);
    setConfirmationDate(new Date());
    setArrivalDate(null);
    setDepartureDate(null);
    setArrivalTime(DEFAULT_ARRIVAL_TIME);
    setDepartureTime(DEFAULT_DEPARTURE_TIME);
    setCheckInTime(DEFAULT_CHECK_IN_TIME);
    setCheckOutTime(DEFAULT_CHECK_OUT_TIME);
    setRoomType('');
    setRateType('daily');
    setRateAmount(0);
    setNumRooms(1);
    setNumGuests(2);
    setDepositAmount(DEFAULT_DEPOSIT_AMOUNT);
    setCurrency('THB');
    setPickUpService(false);
    setDropOffService(false);
    setArrivalFlight('');
    setDepartureFlight('');
    setGuests([{ guest_name: '', passport_number: '', passport_country: '', phone: '', email: '' }]);
    setNumChildren(0);
    setChildren([]);
    setNoticeContent(generateNoticeText(DEFAULT_DEPOSIT_AMOUNT, 2, 0, DEFAULT_ELECTRICITY_RATE));
    setNoticeManuallyEdited(false);
    setIncludedServices([...DEFAULT_INCLUDED_SERVICES]);
    setCancellationPolicy(DEFAULT_CANCELLATION_POLICY);
    setPaymentTermsOption(null);
    setPaymentTermsText('');
    setPaymentDeadline('');
    setPaymentStatusOption(null);
    setPaymentStatusText('');
    setWelcomeMessage(DEFAULT_WELCOME_MESSAGE);
    setRemarks('');
    setElectricityRate(DEFAULT_ELECTRICITY_RATE);
    setWaterRate(DEFAULT_WATER_RATE);
    setFieldVisibility(getDefaultVisibility());
    setInitialReminders([]);
    setActiveTab('basic');
    setCreatedId(null);
    setPaymentSummary(null);
    setPaymentsList([]);
    setRemindersList([]);
    setScheduleList([]);
    setScheduleItems([]);
    setShowPaymentForm(false);
    setShowReminderForm(false);

    setCreatedConfirmationNumber('');
  };

  const generateNoticeText = (deposit: number, guestsNum: number, childrenNum: number, elRate: number) => {
    const totalGuests = guestsNum + childrenNum;
    return `Your reservation includes the exclusive use of the villa, complimentary water supply, high-speed Wi-Fi, basic housekeeping services, as well as the routine maintenance of your private pool and lush gardens.
Check-in time is from 3:00 PM onwards, and we kindly request that you check out by 12:00 PM (Noon).
To ensure a worry-free stay, a fully refundable security deposit of ${deposit ? deposit.toLocaleString() + ' THB' : '[?]'} is required upon check-in. This deposit will be promptly returned to you upon your departure, provided there are no damages or outstanding incidental charges.
Please kindly note that electricity consumption are not included in the rental rate. Electricity will be charged based on your actual consumption at ${elRate || '[?]'} THB per unit (kWh).
To ensure your utmost comfort, the villa strictly accommodates a maximum of ${totalGuests || '[?]'} guests, comprising up to ${guestsNum || '[?]'} adults and ${childrenNum || '[?]'} children.
Any illegal or immoral activities—including but not limited to gambling, prostitution, the use of illicit drugs, and the possession of hazardous materials or weapons—are strictly prohibited on the premises.`;
  };

  // Auto-update notice content when relevant fields change (only in create mode or if not manually edited)
  const [noticeManuallyEdited, setNoticeManuallyEdited] = useState(false);

  useEffect(() => {
    if (mode === 'edit') return; // Don't auto-update in edit mode
    if (noticeManuallyEdited) return; // Don't override manual edits
    setNoticeContent(generateNoticeText(depositAmount, numGuests, numChildren, electricityRate));
  }, [depositAmount, numGuests, numChildren, electricityRate]);

  const handlePropertySelect = (value: string | null) => {
    if (!value) { setPropertyId(null); return; }
    const id = parseInt(value);
    setPropertyId(id);
    const prop = properties.find(p => p.id === id);
    if (prop) {
      setPropertyName(prop.property_name || prop.property_number || '');
      setPropertyAddress(prop.address || prop.region || '');
      setNumRooms(prop.bedrooms || 1);
      setNumGuests((prop.bedrooms || 1) * 2);
      const typeMap: Record<string, string> = {
        villa: 'Villa', apartment: 'Apartment', condo: 'Condo',
        penthouse: 'Penthouse', house: 'House', studio: 'Studio', townhouse: 'Townhouse'
      };
      setRoomType(typeMap[prop.property_type] || prop.property_type || '');
      // Notice will auto-update via useEffect above
      setNoticeManuallyEdited(false);
    }
  };

  const addGuest = () => setGuests([...guests, { guest_name: '', passport_number: '', passport_country: '', phone: '', email: '' }]);
  const removeGuest = (idx: number) => { if (guests.length > 1) setGuests(guests.filter((_, i) => i !== idx)); };
  const updateGuest = (idx: number, field: keyof ConfirmationGuest, value: string) => {
    const updated = [...guests];
    (updated[idx] as any)[field] = value;
    setGuests(updated);
  };

  const addChild = () => { setChildren([...children, { child_name: '', child_age: undefined }]); setNumChildren(numChildren + 1); };
  const removeChild = (idx: number) => { setChildren(children.filter((_, i) => i !== idx)); setNumChildren(Math.max(0, numChildren - 1)); };

  // ===== Payment/Reminder handlers (for edit mode) =====
  const editId = mode === 'edit' ? confirmationId : createdId;

  const fetchPaymentData = async () => {
    if (!editId) return;
    setPaymentLoading(true);
    try {
      // Use Promise.allSettled so one failure doesn't break all
      const [s, p, r, sch] = await Promise.allSettled([
        confirmationsApi.getPaymentSummary(editId),
        confirmationsApi.getPayments(editId),
        confirmationsApi.getReminders(editId),
        confirmationsApi.getPaymentSchedule(editId),
      ]);
      if (s.status === 'fulfilled') setPaymentSummary(s.value.data.data);
      if (p.status === 'fulfilled') setPaymentsList(p.value.data.data || []);
      if (r.status === 'fulfilled') setRemindersList(r.value.data.data || []);
      if (sch.status === 'fulfilled') setScheduleList(sch.value.data.data || []);
    } catch (e) { console.error('Error loading payment data:', e); }
    finally { setPaymentLoading(false); }
  };

  useEffect(() => {
    if (editId && (activeTab === 'payments' || activeTab === 'reminders')) {
      fetchPaymentData();
    }
  }, [editId, activeTab]);

  // savePayment and deletePayment removed — now handled via Payment Schedule

  const saveReminder = async () => {
    if (!editId) return;
    try {
      const rf = reminderForm;
      const data: any = {
        reminder_type: rf.reminder_type,
        days_before: rf.reminder_type === 'days_before' ? rf.days_before : null,
        custom_date: rf.reminder_type === 'custom_date' && rf.custom_date ? dayjs(rf.custom_date).format('YYYY-MM-DD') : null,
        comment: rf.comment || null,
        is_enabled: rf.is_enabled,
        recurrence_type: rf.recurrence_type,
        recurrence_interval: ['every_n_days', 'every_n_weeks', 'every_n_months'].includes(rf.recurrence_type) ? rf.recurrence_interval : null,
        recurrence_day_of_week: rf.recurrence_type === 'weekly' ? rf.recurrence_day_of_week : null,
        day_of_month: ['monthly_by_date', 'every_n_months', 'yearly'].includes(rf.recurrence_type) ? rf.day_of_month : null,
        month_of_year: rf.recurrence_type === 'yearly' ? rf.month_of_year : null,
        recurrence_end_date: rf.recurrence_end_date ? dayjs(rf.recurrence_end_date).format('YYYY-MM-DD') : null,
        recurrence_max_count: rf.recurrence_max_count || null,
      };
      if (editingReminderId) { await confirmationsApi.updateReminder(editId, editingReminderId, data); }
      else { await confirmationsApi.createReminder(editId, data); }
      setShowReminderForm(false); setEditingReminderId(null);
      setReminderForm({ ...defaultReminderForm });
      fetchPaymentData();
    } catch { notifications.show({ title: 'Error', message: 'Failed to save reminder', color: 'red' }); }
  };

  const quickAddReminder = async (type: string, days?: number) => {
    if (!editId) return;
    try { await confirmationsApi.createReminder(editId, { reminder_type: type, days_before: days || null, is_enabled: true, recurrence_type: 'none' }); fetchPaymentData(); }
    catch { notifications.show({ title: 'Error', message: 'Failed', color: 'red' }); }
  };

  const handleSubmit = async () => {
    // Validation
    if (!channel) {
      notifications.show({ title: 'Validation', message: 'Channel is required (Agent or Other)', color: 'orange' });
      setActiveTab('basic');
      return;
    }
    if (!propertyName.trim()) {
      notifications.show({ title: 'Validation', message: 'Property name is required', color: 'orange' });
      setActiveTab('basic');
      return;
    }
    if (!guests.some(g => g.guest_name?.trim())) {
      notifications.show({ title: 'Validation', message: 'At least one guest name is required', color: 'orange' });
      setActiveTab('guests');
      return;
    }

    setLoading(true);
    try {
      const data: CreateConfirmationDTO = {
        property_id: propertyId || undefined,
        property_name: propertyName,
        property_address: propertyAddress,
        from_company_name: fromCompanyName,
        from_telephone: fromTelephone,
        from_email: fromEmail,
        confirmation_date: confirmationDate ? dayjs(confirmationDate).format('YYYY-MM-DD') : undefined,
        arrival_date: arrivalDate ? dayjs(arrivalDate).format('YYYY-MM-DD') : undefined,
        departure_date: departureDate ? dayjs(departureDate).format('YYYY-MM-DD') : undefined,
        arrival_time: arrivalTime,
        departure_time: departureTime,
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        room_type: roomType,
        rate_type: rateType === 'monthly' ? 'monthly' : rateType === 'period' ? 'for_period' : 'per_night',
        rate_amount: rateAmount || undefined,
        num_rooms: numRooms,
        num_guests: numGuests,
        num_children: numChildren,
        deposit_amount: depositAmount || undefined,
        currency,
        pick_up_service: pickUpService ? (arrivalFlight || 'Yes') : undefined,
        drop_off_service: dropOffService ? (departureFlight || 'Yes') : undefined,
        arrival_flight: pickUpService ? arrivalFlight : undefined,
        departure_flight: dropOffService ? departureFlight : undefined,
        remarks: remarks || undefined,
        notice_content: noticeContent || undefined,
        cancellation_policy: cancellationPolicy || undefined,
        payment_terms_option: paymentTermsOption || undefined,
        payment_terms_text: paymentTermsText || undefined,
        payment_deadline: paymentDeadline || undefined,
        payment_status_option: paymentStatusOption || undefined,
        payment_status_text: paymentStatusText || undefined,
        welcome_message: welcomeMessage || undefined,
        included_services: includedServices.filter(s => s.trim()) || undefined,
        electricity_rate: electricityRate,
        water_rate: waterRate,
        field_visibility: fieldVisibility,
        payment_status: paymentStatus,
        agent_name: agentName || undefined,
        channel: channel || undefined,
        guests: guests.filter(g => g.guest_name),
        children: children.filter(c => c.child_name),
      };

      // If we're editing OR we already created this document, update it
      const updateId = confirmationId || createdId;
      if (updateId) {
        await confirmationsApi.update(updateId, data);
        notifications.show({ title: t('common.success'), message: 'Confirmation saved', color: 'green', icon: <IconCheck size={18} /> });
        onCreated?.();
      } else {
        const response = await confirmationsApi.create(data);
        const newId = response.data.data.id;
        setCreatedId(newId);
        setCreatedConfirmationNumber(response.data.data.confirmation_number);

        // Create initial reminders
        if (initialReminders.length > 0 && newId) {
          for (const reminder of initialReminders) {
            try {
              await confirmationsApi.createReminder(newId, {
                reminder_type: reminder.type,
                days_before: reminder.days || null,
                is_enabled: true,
                recurrence_type: 'none',
              });
            } catch (e) {
              console.error('Failed to create initial reminder:', e);
            }
          }
        }

        // Create payment schedule items
        if (scheduleItems.length > 0 && newId) {
          for (const item of scheduleItems) {
            if (item.due_date && item.amount > 0) {
              try {
                await confirmationsApi.createScheduleItem(newId, {
                  due_date: dayjs(item.due_date).format('YYYY-MM-DD'),
                  amount: item.amount,
                  currency: item.currency,
                  description: item.description || null,
                  show_in_document: item.show_in_doc,
                  reminder_enabled: true,
                  reminder_days_before: 3,
                });
              } catch (e) { console.error('Failed to create schedule item:', e); }
            }
          }
        }

        notifications.show({
          title: 'Confirmation Created',
          message: `${response.data.data.confirmation_number} — now you can add payments, upload receipts and configure reminders`,
          color: 'green', icon: <IconCheck size={18} />, autoClose: 5000
        });

        // Switch to payments tab so user can add payments/receipts immediately
        setActiveTab('payments');

        // Notify parent to refresh the list immediately
        onCreated?.();
      }
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || 'Error saving confirmation',
        color: 'red', icon: <IconX size={18} />
      });
    } finally {
      setLoading(false);
    }
  };



  // Success Modal
  return (
    <Modal
      opened={visible}
      onClose={onCancel}
      title={
        <Group gap="sm">
          <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>
            <IconCalendar size={20} />
          </ThemeIcon>
          <Text fw={600}>
            {createdId ? `Confirmation ${createdConfirmationNumber}` : mode === 'edit' ? 'Edit Confirmation' : 'Create Confirmation'}
          </Text>
        </Group>
      }
      size="xl"
      fullScreen={isMobile}
    >
      <ScrollArea h={isMobile ? 'calc(100vh - 140px)' : '70vh'}>
        <Stack gap="lg" p="xs">
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconHome size={14} />}>Basic</Tabs.Tab>
              <Tabs.Tab value="guests" leftSection={<IconUsers size={14} />}>Guests</Tabs.Tab>
              <Tabs.Tab value="details" leftSection={<IconNote size={14} />}>Details</Tabs.Tab>
              <Tabs.Tab value="payments" leftSection={<IconCash size={14} />}>Payments</Tabs.Tab>
              <Tabs.Tab value="reminders" leftSection={<IconBell size={14} />}>Reminders</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <Stack gap="md">
              {/* Channel & Name */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconUserCircle size={16} style={{ verticalAlign: 'middle' }} /> Channel</Text>
                <Stack gap="sm">
                  <Select
                    label="Channel"
                    placeholder="Select channel"
                    data={[
                      { value: 'agent', label: 'Agent' },
                      { value: 'other', label: 'Other' },
                    ]}
                    value={channel}
                    onChange={setChannel}
                    required
                    size="sm"
                    error={!channel ? undefined : undefined}
                  />
                  {channel && (
                    <TextInput
                      label="Name"
                      placeholder={channel === 'agent' ? 'Agent name' : 'Source name'}
                      value={agentName}
                      onChange={e => setAgentName(e.target.value)}
                      size="sm"
                    />
                  )}
                </Stack>
              </Card>

              {/* Property Selection */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconHome size={16} style={{ verticalAlign: 'middle' }} /> Property</Text>
                <SegmentedControl
                  value={propertyInputMode}
                  onChange={(v) => setPropertyInputMode(v as 'select' | 'manual')}
                  data={[
                    { value: 'select', label: 'Select from list' },
                    { value: 'manual', label: 'Enter manually' }
                  ]}
                  mb="sm" fullWidth size="xs"
                />

                {propertyInputMode === 'select' ? (
                  <Select
                    label="Property"
                    placeholder="Select property"
                    data={properties.map(p => ({
                      value: String(p.id),
                      label: `${p.property_name || 'No name'}${p.property_number ? ` - ${p.property_number}` : ''}`
                    }))}
                    value={propertyId ? String(propertyId) : null}
                    onChange={handlePropertySelect}
                    searchable clearable
                    leftSection={<IconHome size={16} />}
                  />
                ) : (
                  <Grid>
                    <Grid.Col span={12}>
                      <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="property_name">
                        <TextInput label="Property Name" placeholder="Villa Serenity" value={propertyName} onChange={e => setPropertyName(e.target.value)} leftSection={<IconHome size={16} />} />
                      </VisField>
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="property_address">
                        <TextInput label="Address" placeholder="Property address" value={propertyAddress} onChange={e => setPropertyAddress(e.target.value)} />
                      </VisField>
                    </Grid.Col>
                  </Grid>
                )}

                {propertyInputMode === 'select' && propertyId && (
                  <Alert variant="light" color="teal" mt="sm" icon={<IconCheck size={16} />}>
                    <Text size="xs">Auto-filled: {numRooms} rooms, {numGuests} guests, {roomType}</Text>
                  </Alert>
                )}

                <Grid mt="sm">
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="room_type">
                      <TextInput label="Type" placeholder="Villa" value={roomType} onChange={e => setRoomType(e.target.value)} size="sm" />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="num_rooms">
                      <NumberInput label="Rooms" value={numRooms} onChange={(v) => { setNumRooms(Number(v) || 1); setNumGuests((Number(v) || 1) * 2); }} min={1} size="sm" />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="num_guests">
                      <NumberInput label="Guests" value={numGuests} onChange={(v) => setNumGuests(Number(v) || 1)} min={1} size="sm" />
                    </VisField>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Contact */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconBuilding size={16} style={{ verticalAlign: 'middle' }} /> From (Company)</Text>
                <Grid>
                  <Grid.Col span={12}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="from_company_name">
                      <TextInput label="Company" value={fromCompanyName} onChange={e => setFromCompanyName(e.target.value)} leftSection={<IconBuilding size={16} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="from_telephone">
                      <TextInput label="Phone" value={fromTelephone} onChange={e => setFromTelephone(e.target.value)} leftSection={<IconPhone size={16} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="from_email">
                      <TextInput label="Email" value={fromEmail} onChange={e => setFromEmail(e.target.value)} leftSection={<IconMail size={16} />} />
                    </VisField>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Dates */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconCalendar size={16} style={{ verticalAlign: 'middle' }} /> Dates & Times</Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="confirmation_date">
                      <Text size="sm" fw={500} mb={4}>Confirmation Date</Text>
                      <DatePicker selected={confirmationDate} onChange={setConfirmationDate} dateFormat="dd/MM/yyyy"
                        portalId={DATEPICKER_PORTAL_ID}
                        customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 4 }}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="arrival_date">
                      <Text size="sm" fw={500} mb={4}>Arrival</Text>
                      <DatePicker selected={arrivalDate} onChange={setArrivalDate} dateFormat="dd/MM/yyyy" minDate={new Date()}
                        portalId={DATEPICKER_PORTAL_ID}
                        customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 4 }}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="departure_date">
                      <Text size="sm" fw={500} mb={4}>Departure</Text>
                      <DatePicker selected={departureDate} onChange={setDepartureDate} dateFormat="dd/MM/yyyy" minDate={arrivalDate || new Date()}
                        portalId={DATEPICKER_PORTAL_ID}
                        customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}><VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="arrival_time"><TextInput label="Arrival Time" value={arrivalTime} onChange={e => setArrivalTime(e.target.value)} size="sm" /></VisField></Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}><VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="departure_time"><TextInput label="Departure Time" value={departureTime} onChange={e => setDepartureTime(e.target.value)} size="sm" /></VisField></Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}><VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="check_in_time"><TextInput label="Check-in" value={checkInTime} onChange={e => setCheckInTime(e.target.value)} size="sm" /></VisField></Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}><VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="check_out_time"><TextInput label="Check-out" value={checkOutTime} onChange={e => setCheckOutTime(e.target.value)} size="sm" /></VisField></Grid.Col>
                </Grid>
              </Card>

              {/* Rates */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconCurrencyBaht size={16} style={{ verticalAlign: 'middle' }} /> Rates</Text>
                <Grid>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="rate_type">
                      <Select label="Rate Type" value={rateType} onChange={v => setRateType((v as any) || 'daily')} data={[{ value: 'daily', label: 'Per Night' }, { value: 'monthly', label: 'Monthly' }, { value: 'period', label: 'For Period' }]} size="sm" />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="rate_amount">
                      <NumberInput label="Rate Amount" value={rateAmount} onChange={v => setRateAmount(Number(v) || 0)} min={0} size="sm" leftSection={<IconCurrencyBaht size={14} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="deposit_amount">
                      <NumberInput label="Deposit" value={depositAmount} onChange={v => setDepositAmount(Number(v) || 0)} min={0} size="sm" />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="currency">
                      <Select label="Currency" value={currency} onChange={v => setCurrency(v || 'THB')} data={['THB', 'USD', 'EUR', 'RUB', 'CNY']} size="sm" />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="electricity_rate">
                      <NumberInput label="Electricity (฿/kWh)" value={electricityRate} onChange={v => setElectricityRate(Number(v) || 0)} size="sm" leftSection={<IconBolt size={14} />} />
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="water_rate">
                      <NumberInput label="Water (฿/unit)" value={waterRate} onChange={v => setWaterRate(Number(v) || 0)} size="sm" leftSection={<IconDroplet size={14} />} />
                    </VisField>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Services */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconCar size={16} style={{ verticalAlign: 'middle' }} /> Transfer Services</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="pick_up_service">
                      <Switch label="Pick-up Service" checked={pickUpService} onChange={e => setPickUpService(e.currentTarget.checked)} />
                      {pickUpService && (
                        <TextInput mt="xs" label="Arrival Flight" placeholder="TG123" value={arrivalFlight} onChange={e => setArrivalFlight(e.target.value)} leftSection={<IconPlane size={14} />} size="sm" />
                      )}
                    </VisField>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="drop_off_service">
                      <Switch label="Drop-off Service" checked={dropOffService} onChange={e => setDropOffService(e.currentTarget.checked)} />
                      {dropOffService && (
                        <TextInput mt="xs" label="Departure Flight" placeholder="TG456" value={departureFlight} onChange={e => setDepartureFlight(e.target.value)} leftSection={<IconPlane size={14} />} size="sm" />
                      )}
                    </VisField>
                  </Grid.Col>
                </Grid>
              </Card>
            </Stack>
          )}

          {/* Step 1: Guests */}
          {activeTab === 'guests' && (
            <Stack gap="md">
              <Card withBorder p="md">
                <Group justify="space-between" mb="md">
                  <Text fw={600} size="sm"><IconUsers size={16} style={{ verticalAlign: 'middle' }} /> Guests</Text>
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addGuest}>Add Guest</Button>
                </Group>
                <Stack gap="sm">
                  {guests.map((guest, idx) => (
                    <Card key={idx} withBorder p="sm" bg="var(--mantine-color-default)">
                      <Group justify="space-between" mb="xs">
                        <Badge size="sm" variant="light">Guest {idx + 1}</Badge>
                        {guests.length > 1 && (
                          <ActionIcon size="sm" color="red" variant="light" onClick={() => removeGuest(idx)}><IconTrash size={12} /></ActionIcon>
                        )}
                      </Group>
                      <Grid>
                        <Grid.Col span={6}><TextInput label="Name" required value={guest.guest_name} onChange={e => updateGuest(idx, 'guest_name', e.target.value)} size="sm" /></Grid.Col>
                        <Grid.Col span={6}><TextInput label="Passport" value={guest.passport_number || ''} onChange={e => updateGuest(idx, 'passport_number', e.target.value)} size="sm" /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Country" value={guest.passport_country || ''} onChange={e => updateGuest(idx, 'passport_country', e.target.value)} size="sm" /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Phone" value={guest.phone || ''} onChange={e => updateGuest(idx, 'phone', e.target.value)} size="sm" /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Email" value={guest.email || ''} onChange={e => updateGuest(idx, 'email', e.target.value)} size="sm" /></Grid.Col>
                      </Grid>
                    </Card>
                  ))}
                </Stack>
              </Card>

              {/* Children */}
              <Card withBorder p="md">
                <Group justify="space-between" mb="md">
                  <Text fw={600} size="sm"><IconBabyCarriage size={16} style={{ verticalAlign: 'middle' }} /> Children</Text>
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addChild}>Add Child</Button>
                </Group>
                {children.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="md">No children added</Text>
                ) : (
                  <Stack gap="sm">
                    {children.map((child, idx) => (
                      <Group key={idx} gap="sm">
                        <TextInput flex={1} label="Name" value={child.child_name} onChange={e => { const u = [...children]; u[idx].child_name = e.target.value; setChildren(u); }} size="sm" />
                        <NumberInput w={80} label="Age" value={child.child_age || undefined} onChange={v => { const u = [...children]; u[idx].child_age = Number(v) || undefined; setChildren(u); }} min={0} max={17} size="sm" />
                        <ActionIcon mt={24} color="red" variant="light" onClick={() => removeChild(idx)}><IconTrash size={14} /></ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Card>
            </Stack>
          )}

          {/* Step 2: Details */}
          {activeTab === 'details' && (
            <Stack gap="md">
              {/* Payment Status Selector */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconCash size={16} style={{ verticalAlign: 'middle' }} /> Payment Status</Text>
                <Text size="xs" c="dimmed" mb="sm">Select the payment status. This controls which payment section appears in the document.</Text>
                <SegmentedControl
                  value={paymentStatus}
                  onChange={(v) => setPaymentStatus(v as 'pending' | 'success')}
                  data={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'success', label: 'Success' }
                  ]}
                  color={paymentStatus === 'success' ? 'green' : 'orange'}
                  fullWidth
                />
              </Card>

              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconNote size={16} style={{ verticalAlign: 'middle' }} /> Important Notice</Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Values auto-fill from your form data. <span style={{ color: 'var(--mantine-color-red-6)', fontWeight: 600 }}>[?]</span> = needs manual input,
                  <span style={{ background: '#d4edda', padding: '0 3px', borderRadius: 2, fontWeight: 600, marginLeft: 4 }}>green</span> = auto-filled.
                  {noticeManuallyEdited && <Badge size="xs" ml="xs" color="yellow" variant="light">Manually edited — auto-update paused</Badge>}
                </Text>

                {/* Preview with highlighted values */}
                <Paper p="sm" mb="sm" radius="sm" withBorder style={{ fontSize: '12px', lineHeight: 1.6 }}>
                  {noticeContent.split('\n').map((line, i) => (
                    <div key={i} style={{ marginBottom: i < noticeContent.split('\n').length - 1 ? 4 : 0 }}>
                      {line.split(/(\[\?\]|\d[\d,]* THB|\d+ adults|\d+ children|\d+ guests|\d+ THB per unit)/g).map((part, j) => {
                        if (part === '[?]') return <span key={j} style={{ background: '#ffe0e0', color: '#c92a2a', padding: '0 3px', borderRadius: 2, fontWeight: 700 }}>[?]</span>;
                        if (/\d[\d,]* THB/.test(part) || /\d+ adults/.test(part) || /\d+ children/.test(part) || /\d+ guests/.test(part) || /\d+ THB per unit/.test(part))
                          return <span key={j} style={{ background: '#d4edda', color: '#1b5e20', padding: '0 3px', borderRadius: 2, fontWeight: 600 }}>{part}</span>;
                        return <span key={j}>{part}</span>;
                      })}
                    </div>
                  ))}
                </Paper>

                <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="notice_content">
                  <Textarea
                    label="Edit Notice Content"
                    value={noticeContent}
                    onChange={e => { setNoticeContent(e.target.value); setNoticeManuallyEdited(true); }}
                    minRows={6} autosize size="sm"
                  />
                </VisField>
                {noticeManuallyEdited && (
                  <Button size="xs" variant="subtle" mt="xs" onClick={() => {
                    setNoticeManuallyEdited(false);
                    setNoticeContent(generateNoticeText(depositAmount, numGuests, numChildren, electricityRate));
                  }}>
                    Reset to auto-generated text
                  </Button>
                )}
              </Card>

              <Card withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600} size="sm"><IconPlus size={16} style={{ verticalAlign: 'middle' }} /> Included Services</Text>
                    <Text size="xs" c="dimmed">Special inclusions shown as separate items below the notice in the document</Text>
                  </div>
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                    onClick={() => setIncludedServices([...includedServices, ''])}>
                    Add
                  </Button>
                </Group>
                <Stack gap="xs">
                  {includedServices.map((service, idx) => (
                    <Group key={idx} gap="xs" wrap="nowrap">
                      <Text size="xs" fw={700} c="dimmed" w={20} ta="center">{idx + 1}</Text>
                      <Textarea
                        flex={1}
                        value={service}
                        onChange={e => {
                          const updated = [...includedServices];
                          updated[idx] = e.target.value;
                          setIncludedServices(updated);
                        }}
                        minRows={1}
                        autosize
                        size="sm"
                        placeholder="Enter included service..."
                      />
                      <ActionIcon variant="subtle" color="red" size="sm"
                        onClick={() => setIncludedServices(includedServices.filter((_, i) => i !== idx))}>
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                  {includedServices.length === 0 && (
                    <Text size="sm" c="dimmed" ta="center" py="sm">No included services. Click "Add" to add one.</Text>
                  )}
                </Stack>
              </Card>

              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconAlertCircle size={16} style={{ verticalAlign: 'middle' }} /> Cancellation Policy</Text>
                <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="cancellation_policy">
                  <Textarea label="Policy" value={cancellationPolicy} onChange={e => setCancellationPolicy(e.target.value)} minRows={2} autosize />
                </VisField>
              </Card>

              {/* Payment Terms — shown when status is Pending */}
              {paymentStatus === 'pending' && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm"><IconCash size={16} style={{ verticalAlign: 'middle' }} /> Payment Terms & Schedule</Text>
                  <Text size="xs" c="dimmed" mb="sm">Shown in document when payment status is Pending</Text>
                  <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="payment_terms">
                    <Stack gap="sm">
                      <Select
                        label="Payment Option"
                        placeholder="Select payment terms template"
                        data={PAYMENT_TERMS_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                        value={paymentTermsOption}
                        onChange={(v) => {
                          setPaymentTermsOption(v);
                          const template = PAYMENT_TERMS_OPTIONS.find(o => o.value === v);
                          if (template) setPaymentTermsText(template.text);
                        }}
                        clearable size="sm"
                      />
                      <Textarea
                        label="Payment Terms Text"
                        description="You can edit. Use [Insert Date/Time] as placeholder for the payment deadline."
                        value={paymentTermsText}
                        onChange={e => setPaymentTermsText(e.target.value)}
                        minRows={4} autosize size="sm"
                      />
                      <div>
                        <Text size="sm" fw={500} mb={4}>Payment Deadline</Text>
                        <Text size="xs" c="dimmed" mb={4}>Select date — replaces [Insert Date/Time] in the text above</Text>
                        <DatePicker
                          selected={paymentDeadline ? new Date(paymentDeadline) : null}
                          onChange={(d: Date | null) => {
                            const formatted = d ? dayjs(d).format('MMMM D, YYYY') : '';
                            setPaymentDeadline(formatted);
                            if (formatted && paymentTermsText) {
                              setPaymentTermsText(prev => prev.replace(/\[Insert Date\/Time\]/g, formatted));
                            }
                          }}
                          dateFormat="dd MMMM yyyy"
                          portalId={DATEPICKER_PORTAL_ID}
                          isClearable
                          customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} placeholder="Select payment deadline date" />}
                        />
                      </div>
                    </Stack>
                  </VisField>
                </Card>
              )}

              {/* Payment Status Text — shown when status is Success */}
              {paymentStatus === 'success' && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm"><IconCheck size={16} style={{ verticalAlign: 'middle' }} /> Payment Status Confirmation</Text>
                  <Text size="xs" c="dimmed" mb="sm">Shown in document when payment status is Success</Text>
                  <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="payment_status_text">
                    <Stack gap="sm">
                      <Select
                        label="Payment Status Template"
                        placeholder="Select payment status"
                        data={PAYMENT_STATUS_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                        value={paymentStatusOption}
                        onChange={(v) => {
                          setPaymentStatusOption(v);
                          const template = PAYMENT_STATUS_OPTIONS.find(o => o.value === v);
                          if (template) setPaymentStatusText(template.text);
                        }}
                        clearable size="sm"
                      />
                      <Textarea
                        label="Payment Status Text"
                        description="You can edit. Use [date] as placeholder for balance due date."
                        value={paymentStatusText}
                        onChange={e => setPaymentStatusText(e.target.value)}
                        minRows={3} autosize size="sm"
                      />
                    </Stack>
                  </VisField>
                </Card>
              )}

              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm">Welcome Message</Text>
                <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="welcome_message">
                  <Textarea label="Message" value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)} minRows={2} autosize />
                </VisField>
              </Card>

              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm">Remarks</Text>
                <VisField fieldVisibility={fieldVisibility} toggleVisibility={toggleVisibility} field="remarks">
                  <Textarea label="Remarks" value={remarks} onChange={e => setRemarks(e.target.value)} minRows={2} autosize />
                </VisField>
              </Card>

              {/* Reminders (only in create mode) */}
              {mode === 'create' && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm"><IconBell size={16} style={{ verticalAlign: 'middle' }} /> Reminders</Text>
                  <Text size="xs" c="dimmed" mb="sm">Select reminders to automatically create with this confirmation</Text>
                  <Stack gap="xs">
                    {[
                      { type: 'check_in_day', label: 'Check-in Day', days: undefined },
                      { type: 'days_before', label: '3 Days Before', days: 3 },
                      { type: 'days_before', label: '7 Days Before', days: 7 },
                      { type: 'days_before', label: '14 Days Before', days: 14 },
                      { type: 'days_before', label: '30 Days Before', days: 30 },
                    ].map((preset) => {
                      const key = `${preset.type}-${preset.days || 0}`;
                      const isSelected = initialReminders.some(r => r.type === preset.type && r.days === preset.days);
                      return (
                        <Checkbox
                          key={key}
                          label={preset.label}
                          checked={isSelected}
                          onChange={() => {
                            if (isSelected) {
                              setInitialReminders(initialReminders.filter(r => !(r.type === preset.type && r.days === preset.days)));
                            } else {
                              setInitialReminders([...initialReminders, { type: preset.type, days: preset.days }]);
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <Text size="xs" c="dimmed" mt="sm">You can also manage reminders from the Reminders tab after saving</Text>
                </Card>
              )}
            </Stack>
          )}

          {/* PAYMENTS TAB */}
          {activeTab === 'payments' && !editId && (
            <Center p="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray"><IconCash size={24} /></ThemeIcon>
                <Text c="dimmed" size="sm" ta="center">Save the document first by going to Details tab and clicking "Save & Next"</Text>
              </Stack>
            </Center>
          )}

          {activeTab === 'payments' && editId && (
            paymentLoading ? <Center p="xl"><Loader size="md" /></Center> : (
            <Stack gap="md">
              {/* Booking Overview */}
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm"><IconHome size={14} style={{ verticalAlign: 'middle' }} /> Booking Overview</Text>
                <Grid>
                  <Grid.Col span={5}><Text size="xs" c="dimmed">Property</Text><Text size="sm" fw={500}>{propertyName || '—'}</Text></Grid.Col>
                  <Grid.Col span={4}><Text size="xs" c="dimmed">Dates</Text><Text size="sm">{arrivalDate ? `${dayjs(arrivalDate).format('DD.MM')} — ${dayjs(departureDate).format('DD.MM')}` : '—'}</Text></Grid.Col>
                  <Grid.Col span={3}><Text size="xs" c="dimmed">Nights</Text><Text size="sm" fw={500}>{arrivalDate && departureDate ? dayjs(departureDate).diff(dayjs(arrivalDate), 'day') : 0}</Text></Grid.Col>
                </Grid>
              </Card>

              {/* Expected Amounts — Other Fees + Room Fees */}
              <Card withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600} size="sm"><IconCash size={14} style={{ verticalAlign: 'middle' }} /> Expected Amounts</Text>
                    <Text size="xs" c="dimmed">Other Fees + Room Fees = Total Fees</Text>
                  </div>
                  <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} onClick={() => {
                    const existing = paymentSummary?.expected_amounts || [];
                    // Group by currency, separate rent/room
                    const currencies = [...new Set(existing.map((e: any) => e.currency))];
                    if (currencies.length === 0) currencies.push('THB');
                    const form = currencies.map(cur => {
                      const rent = existing.find((e: any) => e.currency === cur && (e.fee_type === 'rent' || !e.fee_type));
                      const room = existing.find((e: any) => e.currency === cur && e.fee_type === 'room');
                      return { currency: cur as string, rent: rent ? Number(rent.amount) : 0, room: room ? Number(room.amount) : 0 };
                    });
                    setExpectedForm(form as any);
                    setEditingExpected(true);
                  }}>Edit</Button>
                </Group>
                {editingExpected ? (
                  <Stack gap="sm">
                    {(expectedForm as any[]).map((ea: any, idx: number) => (
                      <Card key={idx} withBorder p="xs" bg="var(--mantine-color-default)">
                        <Grid>
                          <Grid.Col span={4}>
                            <NumberInput label="Other Fees" value={ea.rent || 0} onChange={v => { const u = [...expectedForm] as any[]; u[idx].rent = Number(v) || 0; setExpectedForm(u as any); }} min={0} size="sm" />
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <NumberInput label="Room Fees" value={ea.room || 0} onChange={v => { const u = [...expectedForm] as any[]; u[idx].room = Number(v) || 0; setExpectedForm(u as any); }} min={0} size="sm" />
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Select label="Currency" value={ea.currency} onChange={v => { const u = [...expectedForm] as any[]; u[idx].currency = v || 'THB'; setExpectedForm(u as any); }} data={CURRENCIES} size="sm" />
                          </Grid.Col>
                        </Grid>
                        <Group mt="xs" justify="space-between">
                          <Text size="xs" c="dimmed">Total: <strong>{((ea.rent || 0) + (ea.room || 0)).toLocaleString()} {ea.currency}</strong></Text>
                          {(expectedForm as any[]).length > 1 && <ActionIcon color="red" variant="light" size="xs" onClick={() => setExpectedForm((expectedForm as any[]).filter((_: any, i: number) => i !== idx) as any)}><IconTrash size={12} /></ActionIcon>}
                        </Group>
                      </Card>
                    ))}
                    <Group>
                      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setExpectedForm([...(expectedForm as any[]), { currency: 'THB', rent: 0, room: 0 }] as any)}>Add Currency</Button>
                      <Button size="xs" color="green" leftSection={<IconCheck size={14} />} onClick={async () => {
                        try {
                          // Convert rent/room form to flat array for API
                          const items: any[] = [];
                          for (const ea of (expectedForm as any[])) {
                            if (ea.rent > 0) items.push({ amount: ea.rent, currency: ea.currency, fee_type: 'rent' });
                            if (ea.room > 0) items.push({ amount: ea.room, currency: ea.currency, fee_type: 'room' });
                          }
                          await confirmationsApi.updateExpectedAmounts(editId, items);
                          setEditingExpected(false);
                          fetchPaymentData();
                          notifications.show({ title: 'Success', message: 'Expected amounts saved', color: 'green' });
                        } catch { notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }); }
                      }}>Save</Button>
                      <Button size="xs" variant="subtle" onClick={() => setEditingExpected(false)}>Cancel</Button>
                    </Group>
                  </Stack>
                ) : (
                  <Stack gap="xs">
                    {paymentSummary?.currency_statuses && paymentSummary.currency_statuses.length > 0 ? (
                      paymentSummary.currency_statuses.map((cs: any, idx: number) => (
                        <div key={idx}>
                          <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>{cs.currency}</Text>
                              {cs.rent > 0 && <Badge size="xs" variant="light" color="blue">Other: {cs.rent.toLocaleString()}</Badge>}
                              {cs.room > 0 && <Badge size="xs" variant="light" color="violet">Room: {cs.room.toLocaleString()}</Badge>}
                            </Group>
                            <Text size="sm">{cs.paid.toLocaleString()} / {cs.expected.toLocaleString()}</Text>
                          </Group>
                          <Progress value={cs.expected > 0 ? Math.min(100, (cs.paid / cs.expected) * 100) : 0}
                            color={cs.is_fully_paid ? 'green' : cs.paid > 0 ? 'orange' : 'gray'} size="lg" radius="md"
                            striped={!cs.is_fully_paid && cs.paid > 0} animated={!cs.is_fully_paid && cs.paid > 0} />
                          {cs.remaining > 0 && <Text size="xs" c="dimmed" mt={2}>Remaining: {cs.remaining.toLocaleString()} {cs.currency}</Text>}
                        </div>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed" ta="center">No expected amounts set — click Edit to add</Text>
                    )}
                  </Stack>
                )}
              </Card>

              {/* Payment Schedule */}
              <Card withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600} size="sm"><IconCalendar size={14} style={{ verticalAlign: 'middle' }} /> Payments</Text>
                  <Button size="xs" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }} leftSection={<IconPlus size={14} />}
                    onClick={() => { setShowPaymentForm(true); setPaymentForm({ amount: 0, currency: 'THB', payment_date: new Date(), payment_method: 'bank_transfer', notes: '' }); setPendingFiles([]); }}>
                    Add Payment
                  </Button>
                </Group>

                {/* New Payment Form */}
                {showPaymentForm && (
                  <Card withBorder p="sm" mb="sm" bg="var(--mantine-color-default)">
                    <Text fw={600} size="sm" mb="xs">New Payment</Text>
                    <Grid>
                      <Grid.Col span={{ base: 6, sm: 4 }}>
                        <NumberInput label="Amount" value={paymentForm.amount} onChange={v => setPaymentForm({ ...paymentForm, amount: Number(v) || 0 })} min={0} required size="sm" />
                      </Grid.Col>
                      <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select label="Currency" value={paymentForm.currency} onChange={v => setPaymentForm({ ...paymentForm, currency: v || 'THB' })} data={CURRENCIES} size="sm" />
                      </Grid.Col>
                      <Grid.Col span={{ base: 6, sm: 5 }}>
                        <Text size="sm" fw={500} mb={4}>Date</Text>
                        <DatePicker selected={paymentForm.payment_date} onChange={(d: Date | null) => setPaymentForm({ ...paymentForm, payment_date: d || new Date() })}
                          dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID}
                          customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} />} />
                      </Grid.Col>
                    </Grid>
                    <TextInput label="Description" placeholder="e.g. Deposit, Final payment..." value={paymentForm.notes}
                      onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} size="sm" mt="xs" />
                    <Group gap="sm" mt="xs">
                      <Checkbox size="xs" label="Show in PDF" defaultChecked />
                      <Checkbox size="xs" label="Already paid" checked={paymentAlreadyPaid}
                        onChange={e => setPaymentAlreadyPaid(e.currentTarget.checked)} />
                      <FileButton onChange={(files) => setPendingFiles(files)} accept="image/*,application/pdf" multiple>
                        {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>Attach Receipt</Button>}
                      </FileButton>
                      {pendingFiles.length > 0 && <Badge size="xs" variant="light">{pendingFiles.length} file(s)</Badge>}
                    </Group>
                    <Group mt="sm" justify="flex-end">
                      <Button variant="subtle" size="xs" onClick={() => setShowPaymentForm(false)}>Cancel</Button>
                      <Button color="green" size="xs" leftSection={<IconCheck size={14} />} onClick={async () => {
                        if (!editId || !paymentForm.amount || paymentForm.amount <= 0) {
                          notifications.show({ title: 'Validation', message: 'Amount is required', color: 'orange' });
                          return;
                        }
                        try {
                          const createRes = await confirmationsApi.createScheduleItem(editId, {
                            due_date: dayjs(paymentForm.payment_date).format('YYYY-MM-DD'),
                            amount: paymentForm.amount,
                            currency: paymentForm.currency,
                            description: paymentForm.notes || null,
                            show_in_document: true,
                            reminder_enabled: true,
                            reminder_days_before: 3,
                          });
                          // Получаем созданный schedule item (id из ответа или последний из списка).
                          let createdItemId: number | null = (createRes as any)?.data?.data?.id ?? null;
                          if (!createdItemId) {
                            const schedRes = await confirmationsApi.getPaymentSchedule(editId);
                            const items = schedRes.data.data || [];
                            createdItemId = items[items.length - 1]?.id ?? null;
                          }
                          // Прикрепляем чек
                          if (pendingFiles.length > 0 && createdItemId) {
                            const fd = new FormData(); pendingFiles.forEach(f => fd.append('files', f));
                            await confirmationsApi.uploadScheduleSlip(editId, createdItemId, fd);
                          }
                          // Если отмечено "Already paid" — сразу создаём реальный payment-запись
                          // через markSchedulePaid (она создаёт confirmation_payments + линкует).
                          if (paymentAlreadyPaid && createdItemId) {
                            await confirmationsApi.markSchedulePaid(editId, createdItemId);
                          }
                          setShowPaymentForm(false);
                          setPendingFiles([]);
                          setPaymentAlreadyPaid(false);
                          fetchPaymentData();
                          // обновляем родительский список (Paid колонка) без закрытия модалки
                          onCreated?.();
                          notifications.show({
                            title: 'Success',
                            message: paymentAlreadyPaid ? 'Payment recorded as paid' : 'Payment scheduled',
                            color: 'green'
                          });
                        } catch { notifications.show({ title: 'Error', message: 'Failed to add payment', color: 'red' }); }
                      }}>Save</Button>
                    </Group>
                  </Card>
                )}

                {(() => {
                  // Платежи без привязки к schedule (legacy direct payments).
                  // Без этого блока пользователь не видит уже записанные платежи и
                  // думает что вкладка пустая, хотя в списке Confirmations стоит Paid.
                  const linkedPaymentIds = new Set(
                    scheduleList.map((s: any) => s.payment_id).filter(Boolean)
                  );
                  const orphanPayments = paymentsList.filter(
                    (p: any) => !linkedPaymentIds.has(p.id)
                  );
                  const isEmpty = scheduleList.length === 0 && orphanPayments.length === 0;
                  return isEmpty && !showPaymentForm ? (
                    <Text size="sm" c="dimmed" ta="center" py="sm">No payments yet</Text>
                  ) : (
                    <Stack gap="xs">
                      {orphanPayments.map((p: any) => (
                        <Card key={`pay-${p.id}`} withBorder p="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm">
                              <Badge size="sm" variant="filled" color="green">✓ Paid</Badge>
                              <Text size="sm" fw={500}>{dayjs(p.payment_date).format('DD.MM.YYYY')}</Text>
                              <Text size="sm" fw={700}>{Number(p.amount).toLocaleString()} {p.currency}</Text>
                              {p.notes && <Text size="xs" c="dimmed">— {p.notes}</Text>}
                              {p.payment_method && <Badge size="xs" variant="light" color="gray">{p.payment_method}</Badge>}
                            </Group>
                            <Group gap={4}>
                              <FileButton onChange={async (files: File[]) => {
                                if (files.length > 0 && editId) {
                                  try {
                                    const fd = new FormData(); files.forEach(f => fd.append('files', f));
                                    await confirmationsApi.uploadPaymentFiles(editId, p.id, fd);
                                    fetchPaymentData();
                                  } catch { notifications.show({ title: 'Error', message: 'Upload failed', color: 'red' }); }
                                }
                              }} accept="image/*,application/pdf" multiple>
                                {(props) => <Tooltip label="Upload receipt"><ActionIcon {...props} variant="light" color="blue" size="sm"><IconUpload size={14} /></ActionIcon></Tooltip>}
                              </FileButton>
                              <Tooltip label="Delete"><ActionIcon variant="light" color="red" size="sm" onClick={async () => {
                                try { await confirmationsApi.deletePayment(editId!, p.id); fetchPaymentData(); }
                                catch { notifications.show({ title: 'Error', message: 'Failed', color: 'red' }); }
                              }}><IconTrash size={14} /></ActionIcon></Tooltip>
                            </Group>
                          </Group>
                          {p.files && p.files.length > 0 && (
                            <Group gap="xs" mt={4} wrap="wrap">
                              {p.files.map((f: any) => (
                                <Paper key={f.id} withBorder p={2} radius="sm" style={{ cursor: 'pointer' }} onClick={() => window.open(f.file_path, '_blank')}>
                                  {f.mime_type?.startsWith('image/') ? (
                                    <Image src={f.file_path} alt={f.file_name} w={50} h={50} radius="sm" fit="cover" />
                                  ) : (
                                    <Box w={50} h={50} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <IconPhoto size={20} />
                                    </Box>
                                  )}
                                </Paper>
                              ))}
                            </Group>
                          )}
                        </Card>
                      ))}
                      {scheduleList.map((s: any) => (
                      <Card key={s.id} withBorder p="xs">
                        {editingScheduleId === s.id ? (
                          /* Inline Edit Mode */
                          <Stack gap="xs">
                            <Grid>
                              <Grid.Col span={5}>
                                <Text size="xs" fw={500} mb={2}>Due Date</Text>
                                <DatePicker selected={editScheduleForm.due_date} onChange={(d: Date | null) => setEditScheduleForm({ ...editScheduleForm, due_date: d })}
                                  dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID}
                                  customInput={<TextInput size="xs" leftSection={<IconCalendar size={12} />} readOnly inputMode="none" style={{ cursor: 'pointer' }} />} />
                              </Grid.Col>
                              <Grid.Col span={4}>
                                <Text size="xs" fw={500} mb={2}>Amount</Text>
                                <NumberInput size="xs" value={editScheduleForm.amount} onChange={v => setEditScheduleForm({ ...editScheduleForm, amount: Number(v) || 0 })} min={0} />
                              </Grid.Col>
                              <Grid.Col span={3}>
                                <Text size="xs" fw={500} mb={2}>Currency</Text>
                                <Select size="xs" value={editScheduleForm.currency} onChange={v => setEditScheduleForm({ ...editScheduleForm, currency: v || 'THB' })} data={CURRENCIES} />
                              </Grid.Col>
                            </Grid>
                            <TextInput size="xs" placeholder="Description (e.g. Deposit, Final payment)" value={editScheduleForm.description}
                              onChange={e => setEditScheduleForm({ ...editScheduleForm, description: e.target.value })} />
                            <Group justify="space-between">
                              <Checkbox size="xs" label="Show in PDF" checked={editScheduleForm.show_in_document}
                                onChange={e => setEditScheduleForm({ ...editScheduleForm, show_in_document: e.currentTarget.checked })} />
                              <Group gap="xs">
                                <Button size="xs" variant="subtle" onClick={() => setEditingScheduleId(null)}>Cancel</Button>
                                <Button size="xs" color="green" leftSection={<IconCheck size={12} />} onClick={async () => {
                                  try {
                                    await confirmationsApi.updateScheduleItem(editId!, s.id, {
                                      due_date: editScheduleForm.due_date ? dayjs(editScheduleForm.due_date).format('YYYY-MM-DD') : s.due_date,
                                      amount: editScheduleForm.amount,
                                      currency: editScheduleForm.currency,
                                      description: editScheduleForm.description,
                                      show_in_document: editScheduleForm.show_in_document,
                                    });
                                    setEditingScheduleId(null);
                                    fetchPaymentData();
                                  } catch { notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }); }
                                }}>Save</Button>
                              </Group>
                            </Group>
                          </Stack>
                        ) : (
                          /* View Mode */
                          <>
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm">
                              <Badge size="sm" variant={s.is_paid ? 'filled' : 'light'} color={s.is_paid ? 'green' : 'orange'}
                                style={{ cursor: 'pointer' }}
                                onClick={async () => {
                                  try {
                                    if (s.is_paid) { await confirmationsApi.markScheduleUnpaid(editId!, s.id); }
                                    else { await confirmationsApi.markSchedulePaid(editId!, s.id); }
                                    fetchPaymentData();
                                  } catch { notifications.show({ title: 'Error', message: 'Failed', color: 'red' }); }
                                }}>
                                {s.is_paid ? '✓ Paid' : 'Pending'}
                              </Badge>
                              <Text size="sm" fw={500}>{dayjs(s.due_date).format('DD.MM.YYYY')}</Text>
                              <Text size="sm" fw={700}>{Number(s.amount).toLocaleString()} {s.currency}</Text>
                              {s.description && <Text size="xs" c="dimmed">— {s.description}</Text>}
                            </Group>
                            <Group gap={4}>
                              {s.show_in_document ? <Badge size="xs" variant="light">PDF</Badge> : null}
                              <FileButton onChange={async (file: File | null) => {
                                if (file && editId) {
                                  try {
                                    const fd = new FormData(); fd.append('files', file);
                                    await confirmationsApi.uploadScheduleSlip(editId, s.id, fd);
                                    fetchPaymentData();
                                    notifications.show({ title: 'Success', message: 'Slip uploaded', color: 'green' });
                                  } catch { notifications.show({ title: 'Error', message: 'Upload failed', color: 'red' }); }
                                }
                              }} accept="image/*,application/pdf">
                                {(props) => <Tooltip label="Upload slip"><ActionIcon {...props} variant="light" color="blue" size="sm"><IconUpload size={14} /></ActionIcon></Tooltip>}
                              </FileButton>
                              <Tooltip label="Edit"><ActionIcon variant="light" size="sm" onClick={() => {
                                setEditingScheduleId(s.id);
                                setEditScheduleForm({
                                  amount: Number(s.amount),
                                  currency: s.currency || 'THB',
                                  due_date: s.due_date ? new Date(s.due_date) : null,
                                  description: s.description || '',
                                  show_in_document: !!s.show_in_document,
                                });
                              }}><IconEdit size={14} /></ActionIcon></Tooltip>
                              <Tooltip label="Delete"><ActionIcon variant="light" color="red" size="sm" onClick={async () => {
                                try { await confirmationsApi.deleteScheduleItem(editId!, s.id); fetchPaymentData(); }
                                catch { notifications.show({ title: 'Error', message: 'Failed', color: 'red' }); }
                              }}><IconTrash size={14} /></ActionIcon></Tooltip>
                            </Group>
                          </Group>
                          {/* Slip preview */}
                          {s.slip_file_path && (
                            <Group gap="xs" mt={4}>
                              <Paper withBorder p={2} radius="sm" style={{ cursor: 'pointer' }} onClick={() => window.open(s.slip_file_path, '_blank')}>
                                {s.slip_file_path.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <Image src={s.slip_file_path} alt="Slip" w={50} h={50} radius="sm" fit="cover" />
                                ) : (
                                  <Box w={50} h={50} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <IconPhoto size={20} /><Text size="xs" ml={2}>Slip</Text>
                                  </Box>
                                )}
                              </Paper>
                              <Text size="xs" c="dimmed">{s.slip_file_name || 'Slip'}</Text>
                            </Group>
                          )}
                          </>
                        )}
                      </Card>
                    ))}
                  </Stack>
                );
                })()}
              </Card>
            </Stack>
            )
          )}

          {/* REMINDERS TAB */}
          {activeTab === 'reminders' && !editId && (
            <Center p="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray"><IconBell size={24} /></ThemeIcon>
                <Text c="dimmed" size="sm" ta="center">Save the document first by going to Details tab and clicking "Save & Next"</Text>
              </Stack>
            </Center>
          )}

          {activeTab === 'reminders' && editId && (
            paymentLoading ? <Center p="xl"><Loader size="md" /></Center> : <Stack gap="md">
              <Card withBorder p="md">
                <Text fw={600} size="sm" mb="sm">Quick Add</Text>
                <Group gap="xs" wrap="wrap">
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('check_in_day')}>Check-in Day</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 3)}>3 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 7)}>7 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 14)}>14 Days Before</Button>
                  <Button size="xs" variant="light" onClick={() => quickAddReminder('days_before', 30)}>30 Days Before</Button>
                </Group>
              </Card>

              <Group justify="flex-end">
                <Button size="sm" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }} leftSection={<IconPlus size={16} />}
                  onClick={() => { setShowReminderForm(true); setEditingReminderId(null); setReminderForm({ ...defaultReminderForm }); }}>
                  Custom Reminder
                </Button>
              </Group>

              {showReminderForm && (
                <Card withBorder p="md">
                  <Text fw={600} size="sm" mb="sm">{editingReminderId ? 'Edit Reminder' : 'New Reminder'}</Text>
                  <Stack gap="sm">
                    {/* Trigger Type */}
                    <Select label="When to start" value={reminderForm.reminder_type}
                      onChange={v => setReminderForm({ ...reminderForm, reminder_type: v || 'days_before' })}
                      data={[
                        { value: 'check_in_day', label: 'On Check-in Day' },
                        { value: 'days_before', label: 'Days Before Check-in' },
                        { value: 'custom_date', label: 'Specific Date' },
                      ]} size="sm" />

                    {reminderForm.reminder_type === 'days_before' && (
                      <NumberInput label="How many days before check-in?"
                        value={reminderForm.days_before}
                        onChange={v => setReminderForm({ ...reminderForm, days_before: Number(v) || 1 })}
                        min={1} max={365} size="sm" suffix=" days" />
                    )}

                    {reminderForm.reminder_type === 'custom_date' && (
                      <>
                        <Text size="sm" fw={500} mb={-8}>Start Date</Text>
                        <DatePicker selected={reminderForm.custom_date}
                          onChange={(d: Date | null) => setReminderForm({ ...reminderForm, custom_date: d })}
                          dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID}
                          customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly style={{ cursor: 'pointer' }} />} />
                      </>
                    )}

                    <Divider label="Repeat" labelPosition="center" />

                    {/* Recurrence Type */}
                    <Select label="Repeat" value={reminderForm.recurrence_type}
                      onChange={v => setReminderForm({ ...reminderForm, recurrence_type: v || 'none' })}
                      data={[
                        { value: 'none', label: 'Do not repeat (one-time)' },
                        { value: 'every_n_days', label: 'Every N days' },
                        { value: 'weekly', label: 'Every week (specific day)' },
                        { value: 'every_n_weeks', label: 'Every N weeks' },
                        { value: 'monthly_by_date', label: 'Every month (specific date)' },
                        { value: 'every_n_months', label: 'Every N months (specific date)' },
                        { value: 'yearly', label: 'Every year (specific date)' },
                      ]} size="sm" />

                    {/* Every N days */}
                    {reminderForm.recurrence_type === 'every_n_days' && (
                      <NumberInput label="Repeat every" value={reminderForm.recurrence_interval}
                        onChange={v => setReminderForm({ ...reminderForm, recurrence_interval: Number(v) || 1 })}
                        min={1} max={365} size="sm" suffix=" days" />
                    )}

                    {/* Weekly — choose day of week */}
                    {reminderForm.recurrence_type === 'weekly' && (
                      <Select label="Day of week" value={String(reminderForm.recurrence_day_of_week ?? 1)}
                        onChange={v => setReminderForm({ ...reminderForm, recurrence_day_of_week: Number(v) })}
                        data={[
                          { value: '1', label: 'Monday' }, { value: '2', label: 'Tuesday' },
                          { value: '3', label: 'Wednesday' }, { value: '4', label: 'Thursday' },
                          { value: '5', label: 'Friday' }, { value: '6', label: 'Saturday' },
                          { value: '0', label: 'Sunday' },
                        ]} size="sm" />
                    )}

                    {/* Every N weeks */}
                    {reminderForm.recurrence_type === 'every_n_weeks' && (
                      <NumberInput label="Repeat every" value={reminderForm.recurrence_interval}
                        onChange={v => setReminderForm({ ...reminderForm, recurrence_interval: Number(v) || 1 })}
                        min={1} max={52} size="sm" suffix=" weeks" />
                    )}

                    {/* Monthly — choose day of month */}
                    {reminderForm.recurrence_type === 'monthly_by_date' && (
                      <NumberInput label="Day of month" value={reminderForm.day_of_month}
                        onChange={v => setReminderForm({ ...reminderForm, day_of_month: Number(v) || 1 })}
                        min={1} max={31} size="sm" />
                    )}

                    {/* Every N months — choose interval + day */}
                    {reminderForm.recurrence_type === 'every_n_months' && (
                      <Grid>
                        <Grid.Col span={6}>
                          <NumberInput label="Every" value={reminderForm.recurrence_interval}
                            onChange={v => setReminderForm({ ...reminderForm, recurrence_interval: Number(v) || 1 })}
                            min={1} max={24} size="sm" suffix=" months" />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput label="On day" value={reminderForm.day_of_month}
                            onChange={v => setReminderForm({ ...reminderForm, day_of_month: Number(v) || 1 })}
                            min={1} max={31} size="sm" />
                        </Grid.Col>
                      </Grid>
                    )}

                    {/* Yearly — choose month + day */}
                    {reminderForm.recurrence_type === 'yearly' && (
                      <Grid>
                        <Grid.Col span={6}>
                          <Select label="Month" value={String(reminderForm.month_of_year)}
                            onChange={v => setReminderForm({ ...reminderForm, month_of_year: Number(v) || 1 })}
                            data={[
                              { value: '1', label: 'January' }, { value: '2', label: 'February' },
                              { value: '3', label: 'March' }, { value: '4', label: 'April' },
                              { value: '5', label: 'May' }, { value: '6', label: 'June' },
                              { value: '7', label: 'July' }, { value: '8', label: 'August' },
                              { value: '9', label: 'September' }, { value: '10', label: 'October' },
                              { value: '11', label: 'November' }, { value: '12', label: 'December' },
                            ]} size="sm" />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput label="Day" value={reminderForm.day_of_month}
                            onChange={v => setReminderForm({ ...reminderForm, day_of_month: Number(v) || 1 })}
                            min={1} max={31} size="sm" />
                        </Grid.Col>
                      </Grid>
                    )}

                    {/* End conditions — show only for recurring */}
                    {reminderForm.recurrence_type !== 'none' && (
                      <>
                        <Divider label="End conditions" labelPosition="center" />
                        <Grid>
                          <Grid.Col span={6}>
                            <NumberInput label="Max send count" placeholder="Unlimited"
                              value={reminderForm.recurrence_max_count ?? undefined}
                              onChange={v => setReminderForm({ ...reminderForm, recurrence_max_count: v ? Number(v) : null })}
                              min={1} size="sm" />
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Text size="sm" fw={500} mb={4}>End date</Text>
                            <DatePicker selected={reminderForm.recurrence_end_date}
                              onChange={(d: Date | null) => setReminderForm({ ...reminderForm, recurrence_end_date: d })}
                              dateFormat="dd/MM/yyyy" portalId={DATEPICKER_PORTAL_ID} isClearable
                              customInput={<TextInput size="sm" leftSection={<IconCalendar size={14} />} readOnly style={{ cursor: 'pointer' }} placeholder="No end date" />} />
                          </Grid.Col>
                        </Grid>
                      </>
                    )}

                    <Divider />

                    <Textarea label="Comment (optional)" value={reminderForm.comment}
                      onChange={e => setReminderForm({ ...reminderForm, comment: e.target.value })}
                      size="sm" minRows={1} placeholder="Note for this reminder..." />

                    <Switch label="Enabled" checked={reminderForm.is_enabled}
                      onChange={e => setReminderForm({ ...reminderForm, is_enabled: e.currentTarget.checked })} />

                    <Group justify="flex-end">
                      <Button variant="subtle" size="sm" onClick={() => setShowReminderForm(false)}>Cancel</Button>
                      <Button color="green" size="sm" leftSection={<IconCheck size={14} />} onClick={saveReminder}>
                        {editingReminderId ? 'Update' : 'Create'}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              {remindersList.length === 0 ? (
                <Center p="lg">
                  <Stack align="center" gap="sm">
                    <ThemeIcon size={48} radius="xl" variant="light" color="gray"><IconBell size={24} /></ThemeIcon>
                    <Text c="dimmed" size="sm">No reminders set — use Quick Add or create a custom one</Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="sm">
                  {remindersList.map(r => (
                    <Card key={r.id} withBorder p="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm">
                          <ThemeIcon size="md" variant="light" color={r.is_enabled ? 'teal' : 'gray'}>
                            <IconBell size={16} />
                          </ThemeIcon>
                          <div>
                            <Group gap={4} mb={2}>
                              <Badge size="xs" variant="light" color="blue">
                                {r.reminder_type === 'check_in_day' ? 'Check-in Day' : r.reminder_type === 'days_before' ? `${r.days_before} days before` : `${dayjs(r.custom_date).format('DD.MM.YYYY')}`}
                              </Badge>
                              {r.recurrence_type !== 'none' && (
                                <Badge size="xs" variant="light" color="violet">
                                  {r.recurrence_type === 'every_n_days' ? `Every ${r.recurrence_interval} day(s)` :
                                   r.recurrence_type === 'weekly' ? 'Weekly' :
                                   r.recurrence_type === 'every_n_weeks' ? `Every ${r.recurrence_interval} week(s)` :
                                   r.recurrence_type === 'monthly_by_date' ? `Monthly (day ${(r as any).day_of_month || '?'})` :
                                   r.recurrence_type === 'every_n_months' ? `Every ${r.recurrence_interval} month(s) (day ${(r as any).day_of_month || '?'})` :
                                   r.recurrence_type === 'yearly' ? 'Yearly' :
                                   r.recurrence_type.replace(/_/g, ' ')}
                                </Badge>
                              )}
                              {r.is_sent && <Badge size="xs" color="green" variant="filled">Completed</Badge>}
                              {!r.is_enabled && !r.is_sent && <Badge size="xs" color="gray" variant="light">Disabled</Badge>}
                            </Group>
                            {r.next_send_date && !r.is_sent && (
                              <Text size="xs" c="dimmed"><IconCalendar size={10} style={{ verticalAlign: 'middle' }} /> Next: {dayjs(r.next_send_date).format('DD.MM.YYYY')}</Text>
                            )}
                            {r.recurrence_sent_count ? <Text size="xs" c="dimmed">Sent {r.recurrence_sent_count} time(s){r.recurrence_max_count ? ` / max ${r.recurrence_max_count}` : ''}</Text> : null}
                            {r.comment && <Text size="xs" c="dimmed" mt={2}>{r.comment}</Text>}
                          </div>
                        </Group>
                        <Group gap={4}>
                          <Tooltip label={r.is_enabled ? 'Disable' : 'Enable'}>
                            <Switch size="xs" checked={!!r.is_enabled} onChange={async () => {
                              try { await confirmationsApi.updateReminder(editId!, r.id, { is_enabled: !r.is_enabled }); fetchPaymentData(); }
                              catch { notifications.show({ title: 'Error', message: 'Failed to toggle', color: 'red' }); }
                            }} />
                          </Tooltip>
                          <Tooltip label="Edit"><ActionIcon variant="light" size="sm" onClick={() => {
                            setReminderForm({
                              reminder_type: r.reminder_type, days_before: r.days_before || 7,
                              custom_date: r.custom_date ? new Date(r.custom_date) : null,
                              comment: r.comment || '', is_enabled: !!r.is_enabled,
                              recurrence_type: r.recurrence_type || 'none',
                              recurrence_interval: r.recurrence_interval || 1,
                              recurrence_end_date: r.recurrence_end_date ? new Date(r.recurrence_end_date) : null,
                              recurrence_max_count: r.recurrence_max_count || null,
                              day_of_month: (r as any).day_of_month || 1,
                              month_of_year: (r as any).month_of_year || 1,
                              recurrence_day_of_week: r.recurrence_day_of_week ?? null,
                            });
                            setEditingReminderId(r.id); setShowReminderForm(true);
                          }}><IconEdit size={14} /></ActionIcon></Tooltip>
                          <Tooltip label="Delete"><ActionIcon variant="light" color="red" size="sm" onClick={() => {
                            modals.openConfirmModal({ title: 'Delete Reminder', children: <Text size="sm">Are you sure you want to delete this reminder?</Text>,
                              labels: { confirm: 'Delete', cancel: 'Cancel' }, confirmProps: { color: 'red' },
                              onConfirm: async () => {
                                try { await confirmationsApi.deleteReminder(editId!, r.id); fetchPaymentData(); }
                                catch { notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' }); }
                              }
                            });
                          }}><IconTrash size={14} /></ActionIcon></Tooltip>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </ScrollArea>

      {/* Footer */}
      <Divider my="sm" />
      <Group justify="space-between">
        <Button variant="subtle" onClick={() => {
          if (createdId || mode === 'edit') { onSuccess(); } else { onCancel(); }
        }} leftSection={<IconX size={16} />}>
          {(createdId || mode === 'edit') ? 'Close' : t('common.cancel')}
        </Button>

        <Group gap="sm">
          {/* Next buttons for stepping through tabs */}
          {activeTab === 'basic' && (
            <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}
              onClick={() => setActiveTab('guests')}>
              Next
            </Button>
          )}
          {activeTab === 'guests' && (
            <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}
              onClick={() => setActiveTab('details')}>
              Next
            </Button>
          )}
          {activeTab === 'details' && (
            <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}
              loading={loading} onClick={async () => {
                await handleSubmit();
                // After save, switch to payments tab
                if (!loading) setActiveTab('payments');
              }}>
              {(confirmationId || createdId) ? 'Save & Next' : 'Save & Next'}
            </Button>
          )}
          {(activeTab === 'payments' || activeTab === 'reminders') && (
            <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}
              loading={loading} leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}>
              Save
            </Button>
          )}
        </Group>
      </Group>
    </Modal>
  );
};

export default CreateConfirmationModal;
